import React, { useMemo, useState } from 'react'
import Breadcrumbs from '../components/Breadcrumbs'
import { createPortal } from 'react-dom'
import Card from '../components/Card'
import AvatarUpload from '../components/AvatarUpload'
import { UserStore } from '../store/userStore'
import { ReactSVG } from 'react-svg'
import BtnIcon from '../components/BtnIcon'
import Skeleton from 'react-loading-skeleton'
import { useAxios } from '../hooks/useAxios'
import { getUserEducationList } from '../api/getUserEducationList'
import { toast } from 'react-toastify'
import EducationInfoItem from '../components/EducationInfoItem'
import { type IEducationItem } from '../server/user/info/education/list/get'
import { getUserLanguagesList } from '../api/getUserLanguagesList'
import Chip from '../components/Chip'
import { type ILanguageItem } from '../server/user/info/languages/list/get'
import { getUserOtherInfo } from '../api/getUserOtherInfo'
import ExpandedText from '../components/ExpandedText'
import { getUserSummaryInfo } from '../api/getUserSummaryInfo'
import { type ISummaryInfo } from '../server/user/info/summary/get'
import { observer } from 'mobx-react-lite'
import { ConfigStore } from '../store/configStore'
import { IUserSocial } from '../server/user/get'
import { AuthStore } from './../store/authStore'
import Balls from './../components/Balls'

const ProfilePage = observer(() => {
  const [portal, setPortal] = useState<HTMLElement | null>(null)
  const BREADCRUMBS = [
    {
      label: 'Главная',
      path: '/'
    },
    {
      label: 'Профиль',
      path: '/profile'
    }
  ]
  const items = [
    {
      label: 'Пол',
      value: UserStore.data.gender
    },
    {
      label: 'Дата рождения',
      value: UserStore.data.birthday
    },
    {
      label: 'СНИЛС',
      value: UserStore.data.snils
    },
    {
      label: 'Телефон',
      value: UserStore.data.phone
    },
    {
      label: 'Электронная почта',
      value: UserStore.data.email
    }
  ]

  const { response, fire } = useAxios()
  const { response: response2, fire: fire2 } = useAxios()
  const { response: response3, fire: fire3 } = useAxios()
  const { response: response4, fire: fire4 } = useAxios()
  const [loading, setLoading] = useState(true)

  React.useEffect(() => {
    const portalTarget = document.getElementById('header-portal')

    if (portalTarget) {
      setPortal(portalTarget)
    }

    setLoading(true)

    const prom1 = fire(getUserEducationList())
    const prom2 = fire2(getUserLanguagesList())
    const prom3 = fire3(getUserOtherInfo())
    const prom4 = fire4(getUserSummaryInfo())

    void Promise.all([prom1, prom2, prom3, prom4]).then((values) => {
      values.forEach(({ error }) => {
        if (error) {
          toast.error(error.message)
        }
      })

      setLoading(false)
    })
  }, [])

  const vendors = useMemo(
    () => ConfigStore.privateConfig?.socialsVendors || [],
    [ConfigStore.privateConfig]
  )

  const userSocials = useMemo<{ [key: string]: IUserSocial }>(() => {
    const userSocials = UserStore.data?.socials || []

    return userSocials.reduce(
      (acc: { [key: string]: IUserSocial }, userSocial: IUserSocial) => {
        const hasVendor = vendors.find(({ id }) => id === userSocial.id)

        if (hasVendor) {
          acc[userSocial.id] = userSocial
        }

        return acc
      },
      {}
    )
  }, [ConfigStore.privateConfig, UserStore.data])

  return (
    <div className={'page'}>
      <Breadcrumbs
        className={'page__breadcrumbs'}
        breadcrumbs={BREADCRUMBS}
      ></Breadcrumbs>
      {portal &&
        createPortal(<Breadcrumbs breadcrumbs={BREADCRUMBS} />, portal)}

      <header className="page__head">
        <h1>Профиль</h1>
      </header>

      <div className="page__content profile-page">
        <div className="profile-page__row">
          <Card className="profile-page-card">
            <div className="profile-page-card__wrapper">
              <AvatarUpload></AvatarUpload>
              <div>
                <header className={'card-head'}>
                  <div className={'card-head__title text-h3'}>
                    {UserStore.data.name} {UserStore.data.lastName}
                  </div>

                  {AuthStore.isUserAuthenticated ? (
                    <BtnIcon
                      to={'/profile/edit'}
                      className={'card-head__control'}
                      filled
                    >
                      <ReactSVG
                        src={'/i/svg/pencil.svg'}
                        wrapper={'span'}
                      ></ReactSVG>
                    </BtnIcon>
                  ) : UserStore.data.balls !== undefined ? (
                    <div className={'profile-page-card__balls'}>
                      <Balls
                        balls={UserStore.data.balls}
                        icon="/i/svg/rating.svg"
                      ></Balls>
                    </div>
                  ) : null}
                </header>
                <div className="card-subtitle text-body-small text-secondary-color">
                  {UserStore.data.role}
                </div>

                <div className="card-content">
                  <div className="profile-page-card__items">
                    {items.map((item, index) => {
                      return item.value ? (
                        <div className="item" key={index}>
                          <div className="item__label text-caption">
                            {item.label}
                          </div>
                          <div className="item__value text-body-bold">
                            {item.value}
                          </div>
                        </div>
                      ) : undefined
                    })}
                  </div>
                  <div className="profile-page-card__socials">
                    {vendors.map((vendor) => {
                      const userSocial = userSocials[vendor.id as string]
                      if (!userSocial) return undefined

                      const Tag = userSocial.href ? 'a' : 'div'

                      return (
                        <Tag
                          key={vendor.id}
                          href={userSocial.href || undefined}
                          target={userSocial.href ? '_blank' : undefined}
                          rel={
                            userSocial.href ? 'noopener noreferrer' : undefined
                          }
                        >
                          <img src={vendor.icon} alt={vendor.name} />
                          <span>{userSocial.text}</span>
                        </Tag>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="profile-page__row">
          <div className="profile-page__col">
            <Card className={'profile-page-card'}>
              {loading ? (
                <>
                  <div>
                    <header className={'card-head d-block'}>
                      <Skeleton width={'50%'}></Skeleton>
                    </header>

                    <div className="card-content">
                      <Skeleton count={2}></Skeleton>
                    </div>
                  </div>
                  <div className={'mt-md-12 mt-sm-10'}>
                    <header className={'card-head d-block'}>
                      <Skeleton width={'50%'}></Skeleton>
                    </header>

                    <div className="card-content">
                      <Skeleton count={2}></Skeleton>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <header className={'card-head'}>
                    <div className={'card-head__title text-h3'}>
                      Образование
                    </div>

                    {AuthStore.isUserAuthenticated && (
                      <BtnIcon
                        to={'/profile/edit/education'}
                        className={'card-head__control'}
                        filled
                      >
                        <ReactSVG
                          src={'/i/svg/pencil.svg'}
                          wrapper={'span'}
                        ></ReactSVG>
                      </BtnIcon>
                    )}
                  </header>
                  <div className="card-content profile-page-card__education">
                    {response?.data?.length ? (
                      response.data.map((item: IEducationItem) => {
                        return (
                          <EducationInfoItem
                            item={item}
                            key={item.id}
                          ></EducationInfoItem>
                        )
                      })
                    ) : (
                      <p className={'text-caption text-secondary-color'}>
                        Заполните информацию об образовании
                      </p>
                    )}
                  </div>
                </>
              )}
            </Card>

            <Card className={'profile-page-card'}>
              {loading ? (
                <>
                  <div>
                    <header className={'card-head d-block'}>
                      <Skeleton width={'50%'}></Skeleton>
                    </header>

                    <div className="card-content">
                      <Skeleton count={2}></Skeleton>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <header className={'card-head'}>
                    <div className={'card-head__title text-h3'}>
                      Знание языков
                    </div>
                    {AuthStore.isUserAuthenticated && (
                      <BtnIcon
                        to={'/profile/edit/language'}
                        className={'card-head__control'}
                        filled
                      >
                        <ReactSVG
                          src={'/i/svg/pencil.svg'}
                          wrapper={'span'}
                        ></ReactSVG>
                      </BtnIcon>
                    )}
                  </header>
                  <div className={'card-content'}>
                    {response2?.data?.length ? (
                      <div className={'row row-gap-8'}>
                        {response2.data.map((item: ILanguageItem) => {
                          return (
                            <Chip className={'text-body-small'} key={item.id}>
                              <>
                                {item.name.label}{' '}
                                <span className={'text-secondary-color'}>
                                  {item.level.label}
                                </span>
                              </>
                            </Chip>
                          )
                        })}
                      </div>
                    ) : (
                      <p className={'text-caption text-secondary-color'}>
                        Заполните информацию о знании языков
                      </p>
                    )}
                  </div>
                </>
              )}
            </Card>
          </div>
          <div className="profile-page__col">
            {loading || response3?.data?.about ? (
              <Card className={'profile-page-card'}>
                {loading ? (
                  <>
                    <header className={'card-head d-block'}>
                      <Skeleton width={'35%'}></Skeleton>
                    </header>
                    <div className="card-content">
                      <Skeleton count={3}></Skeleton>
                    </div>
                  </>
                ) : (
                  <>
                    <header className={'card-head'}>
                      <div className={'card-head__title text-h3'}>О себе</div>
                      {AuthStore.isUserAuthenticated && (
                        <BtnIcon
                          to={'/profile/edit/features'}
                          className={'card-head__control'}
                          filled
                        >
                          <ReactSVG
                            src={'/i/svg/pencil.svg'}
                            wrapper={'span'}
                          ></ReactSVG>
                        </BtnIcon>
                      )}
                    </header>
                    <div className={'card-content'}>
                      <ExpandedText
                        text={response3?.data?.about}
                        limit={250}
                      ></ExpandedText>
                    </div>
                  </>
                )}
              </Card>
            ) : null}
            {loading ? (
              <Card>
                <header className={'card-head d-block'}>
                  <Skeleton width={'35%'}></Skeleton>
                </header>
                <div className="card-content">
                  <Skeleton count={4}></Skeleton>
                </div>
              </Card>
            ) : response4?.data?.length ? (
              response4.data.map((item: ISummaryInfo) => {
                return (
                  <Card className={'profile-page-card'} key={item.title}>
                    <header className={'card-head'}>
                      <div className={'card-head__title text-h3'}>
                        {item.title}
                      </div>
                    </header>
                    <div className={'card-content'}>
                      <div className={'profile-page-card__items _rows'}>
                        {item.fields.map((field) => {
                          return (
                            <div key={field.value} className={'item'}>
                              <div
                                className={'item__label text-body-small-bold'}
                              >
                                {field.label}
                              </div>
                              <div className={'item__value text-body-small'}>
                                {field.value}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </Card>
                )
              })
            ) : (
              <Card>
                <header className={'card-head'}>
                  <div className={'card-head__title text-h3'}>Сведения</div>
                </header>
                <div className={'card-content'}>
                  <p className={'text-caption text-secondary-color'}>
                    Сведения о пользователе не найдены
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  )
})

export default ProfilePage
